import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mediaAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import {
  Upload, Trash2, Copy, Mail, Phone, Play, X, FolderPlus,
  Loader2, Download, Link2, ChevronRight,
  Folder, FolderOpen, ArrowLeft, UploadCloud,
  ChevronLeft, Eye, ZoomIn, ZoomOut, RotateCw, MapPin, RefreshCw, AlertCircle,
  HardDrive, Camera, Video, FileText, CheckCircle2, Pause, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Error Boundary for upload section
class UploadErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(err) { return { hasError: true, error: err }; }
  componentDidCatch(err, info) { console.error('MediaPage render error:', err?.message, info?.componentStack?.slice(0, 200)); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center my-4">
          <p className="text-red-400 text-sm mb-1">Erro ao renderizar midias</p>
          <p className="text-white/30 text-[10px] font-mono mb-3">{this.state.error?.message || 'Erro desconhecido'}</p>
          <button onClick={() => { this.setState({ hasError: false, error: null }); }} className="px-4 py-1.5 bg-cyan-500 text-white rounded-lg text-xs mr-2">TENTAR NOVAMENTE</button>
          <button onClick={() => window.location.reload()} className="px-4 py-1.5 bg-white/10 text-white/60 rounded-lg text-xs">RECARREGAR PAGINA</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_CONCURRENT_UPLOADS = 3;
const PARALLEL_CHUNKS = 2;
const MAX_RETRIES = 5;

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const VALID_EXTS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg', 'mrk', 'nav', 'obs', 'rtk', 'bin', 'pdf'];
const PHOTO_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg'];
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v'];

const getMediaType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (PHOTO_EXTS.includes(ext)) return 'photo';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  return 'file';
};

// ===== UPLOAD QUEUE ITEM (Dark Theme) =====
const UploadQueueItem = ({ item, onRetry, index }) => {
  // Don't render file object - only use safe serializable fields
  const { name, size, type, status, progress, speed, errorLabel } = item;
  const statusBadge = status === 'done' ? 'OK'
    : status === 'error' ? (errorLabel || 'ERRO').slice(0, 12).toUpperCase()
    : status === 'retrying' ? 'RETRY'
    : `${progress}%`;
  return (
    <div className={`flex items-center gap-3 py-2 px-3 rounded-lg ${status === 'error' ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/5'}`} data-testid={`upload-item-${name}`}>
      <div className="flex-shrink-0">
        {status === 'error' ? <AlertCircle size={14} className="text-red-400" /> :
         status === 'done' ? <CheckCircle2 size={14} className="text-emerald-400" /> :
         type === 'photo' ? <Camera size={14} className="text-cyan-400" /> :
         type === 'video' ? <Video size={14} className="text-purple-400" /> :
         <FileText size={14} className="text-amber-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white/90 text-[11px] truncate font-medium">{name}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${
              status === 'done' ? 'bg-emerald-400' : status === 'error' ? 'bg-red-400' : status === 'retrying' ? 'bg-amber-400' : 'bg-cyan-400'
            }`} style={{ width: `${progress}%` }} />
          </div>
          <span className={`text-[9px] font-mono flex-shrink-0 w-20 text-right truncate ${
            status === 'done' ? 'text-emerald-400' : status === 'error' ? 'text-red-400' : status === 'retrying' ? 'text-amber-400' : 'text-white/50'
          }`} title={status === 'error' && errorLabel ? errorLabel : ''}>
            {statusBadge}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-[9px] font-mono text-white/30">{formatSize(size)}</span>
        {speed > 0 && status === 'uploading' && (
          <span className="text-[9px] font-mono text-cyan-400">{formatSize(speed)}/s</span>
        )}
        {status === 'error' && onRetry && (
          <button onClick={() => onRetry(index)} className="w-6 h-6 rounded bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-colors" data-testid={`retry-btn-${index}`} title="Tentar novamente">
            <RefreshCw size={10} className="text-red-400" />
          </button>
        )}
      </div>
    </div>
  );
};

// ===== PHOTO CARD (Dark) =====
const PhotoCard = ({ item, token, canDelete, onDelete, onShare, onView }) => {
  const [loaded, setLoaded] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  if (!item) return null;
  const thumbUrl = mediaAPI.thumbnailUrl(item.id, token);
  const filename = item.original_filename || item.name || 'sem_nome';
  const hasGps = item.gps && typeof item.gps === 'object' && typeof item.gps.lat === 'number';
  return (
    <div className="bg-[#1a2332] rounded-xl overflow-hidden group cursor-pointer border border-white/5 hover:border-cyan-500/30 transition-all hover:shadow-lg hover:shadow-cyan-500/5" data-testid={`photo-card-${item.id}`} onClick={() => onView && onView(item)}>
      <div className="aspect-square bg-[#0d1520] relative overflow-hidden">
        {!loaded && !imgErr && <div className="absolute inset-0 flex items-center justify-center"><Camera size={20} className="text-white/10" /></div>}
        {imgErr && <div className="absolute inset-0 flex items-center justify-center flex-col gap-1"><Camera size={20} className="text-red-500/30" /><span className="text-white/20 text-[8px]">Erro</span></div>}
        {!imgErr && <img src={thumbUrl} alt={filename} className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`} loading="lazy" onLoad={() => setLoaded(true)} onError={() => setImgErr(true)} />}
        {hasGps && <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded text-[8px] text-emerald-400 font-mono"><MapPin size={8} /> GPS</div>}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-cyan-500/80 backdrop-blur-sm items-center justify-center hidden group-hover:flex"><Eye size={16} className="text-white" /></div>
        </div>
      </div>
      <div className="p-2.5">
        <p className="text-white/90 text-[11px] font-medium truncate">{filename}</p>
        <div className="flex items-center gap-2 text-white/30 text-[9px] font-mono mt-1">
          <span>{formatSize(item.size || 0)}</span>
          {item.created_at && <span>{formatDate(item.created_at)}</span>}
        </div>
        <div className="flex gap-1 mt-2 pt-1.5 border-t border-white/5">
          {item.share_token && <button className="text-cyan-400/60 hover:text-cyan-400 text-[9px] flex items-center gap-0.5 flex-1" onClick={(e) => { e.stopPropagation(); onShare && onShare(item); }}><Link2 size={9} /> LINK</button>}
          <a href={mediaAPI.streamUrl(item.id, token)} download onClick={(e) => e.stopPropagation()} className="text-white/30 hover:text-emerald-400 p-0.5"><Download size={10} /></a>
          {canDelete && <button className="text-white/20 hover:text-red-400 p-0.5" onClick={(e) => { e.stopPropagation(); onDelete && onDelete(item); }} data-testid={`delete-photo-${item.id}`}><Trash2 size={10} /></button>}
        </div>
      </div>
    </div>
  );
};

// ===== VIDEO CARD (Dark) =====
const VideoCard = ({ item, token, canDelete, onDelete, onShare, onPlay }) => {
  const handlePlay = useCallback(() => {
    if (!item) return;
    // Fire-and-forget prewarm to ensure cache hit on /stream
    mediaAPI.prewarm(item.id).catch(() => {});
    onPlay && onPlay(item);
  }, [item, onPlay]);
  
  if (!item) return null;
  const streamUrl = mediaAPI.streamUrl(item.id, token);
  const isProcessing = item.transcode_status === 'processing';
  const filename = item.original_filename || item.name || 'video';
  
  return (
    <div className="bg-[#1a2332] rounded-xl overflow-hidden group border border-white/5 hover:border-purple-500/30 transition-all" data-testid={`video-card-${item.id}`}>
      <div className="aspect-video bg-black relative cursor-pointer" onClick={handlePlay}>
        <video src={streamUrl} className="w-full h-full object-contain" preload="metadata" muted playsInline
          onMouseEnter={(e) => { try { e.target.play().catch(() => {}); } catch {} }}
          onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch {} }} />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-purple-500/80 backdrop-blur-sm flex items-center justify-center"><Play size={18} className="text-white ml-0.5" /></div>
        </div>
        {isProcessing && <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-black/70 rounded text-[9px] text-amber-400 font-mono"><Loader2 size={9} className="animate-spin" /> PROCESSANDO</div>}
      </div>
      <div className="p-2.5">
        <p className="text-white/90 text-[11px] font-medium truncate">{filename}</p>
        <div className="flex items-center gap-2 text-white/30 text-[9px] font-mono mt-1">
          <span>{formatSize(item.size || 0)}</span>
          {item.created_at && <span>{formatDate(item.created_at)}</span>}
        </div>
        <div className="flex gap-1 mt-2 pt-1.5 border-t border-white/5">
          {item.share_token && <button className="text-cyan-400/60 hover:text-cyan-400 text-[9px] flex items-center gap-0.5 flex-1" onClick={() => onShare && onShare(item)}><Link2 size={9} /> LINK</button>}
          <button className="text-purple-400/60 hover:text-purple-400 text-[9px] flex items-center gap-0.5" onClick={handlePlay}><Play size={9} /> PLAY</button>
          <a href={streamUrl} download className="text-white/30 hover:text-emerald-400 p-0.5"><Download size={10} /></a>
          {canDelete && <button className="text-white/20 hover:text-red-400 p-0.5" onClick={() => onDelete && onDelete(item)} data-testid={`delete-video-${item.id}`}><Trash2 size={10} /></button>}
        </div>
      </div>
    </div>
  );
};

// ===== FOLDER CARD (Dark) =====
const FolderCard = ({ item, canDelete, onOpen, onDelete, onShare }) => {
  if (!item) return null;
  return (
    <div className="bg-[#1a2332] rounded-xl overflow-hidden group cursor-pointer border border-white/5 hover:border-amber-500/30 transition-all" onClick={() => onOpen && onOpen(item)} data-testid={`folder-card-${item.id}`}>
      <div className="p-5 flex flex-col items-center justify-center gap-2 min-h-[130px]">
        <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
          <FolderOpen size={28} className="text-amber-400" />
        </div>
        <p className="text-white/90 text-sm font-medium text-center truncate w-full">{item.name || 'Pasta'}</p>
        <span className="text-white/30 text-[10px] font-mono">{item.child_count || 0} itens</span>
      </div>
      <div className="flex gap-1 px-2 pb-2 border-t border-white/5 pt-1.5">
        {item.share_token && <button className="text-cyan-400/60 hover:text-cyan-400 text-[9px] flex items-center gap-0.5 flex-1" onClick={(e) => { e.stopPropagation(); onShare && onShare(item); }}><Link2 size={9} /> LINK</button>}
        {canDelete && <button className="text-white/20 hover:text-red-400 p-0.5" onClick={(e) => { e.stopPropagation(); onDelete && onDelete(item); }} data-testid={`delete-folder-${item.id}`}><Trash2 size={10} /></button>}
      </div>
    </div>
  );
};

// ===== PHOTO LIGHTBOX =====
const PhotoLightbox = ({ photo, token, onClose, items, onNavigate }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  if (!photo) return null;
  const currentIndex = items?.findIndex(i => i.id === photo.id) ?? -1;
  const filename = photo.original_filename || photo.name || 'foto';
  const hasGps = photo.gps && typeof photo.gps === 'object' && typeof photo.gps.lat === 'number';
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-black/95 border-none max-w-[95vw] max-h-[95vh] p-0 overflow-hidden" data-testid="photo-lightbox">
        <DialogHeader className="sr-only"><DialogTitle>{photo.original_filename}</DialogTitle></DialogHeader>
        <div className="relative w-full h-[85vh] flex items-center justify-center">
          <button onClick={onClose} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="close-lightbox"><X size={18} /></button>
          {currentIndex > 0 && <button onClick={() => onNavigate(items[currentIndex - 1])} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>}
          {currentIndex < items.length - 1 && <button onClick={() => onNavigate(items[currentIndex + 1])} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="lightbox-next"><ChevronRight size={22} /></button>}
          <img src={mediaAPI.streamUrl(photo.id, token)} alt={filename} className="max-w-full max-h-full object-contain transition-transform duration-200" style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }} data-testid="lightbox-image" />
        </div>
        <div className="px-4 py-2 bg-[#0a1628] flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <p className="text-white text-sm truncate">{filename}</p>
            {hasGps && <span className="flex items-center gap-1 text-emerald-400 text-[10px] font-mono flex-shrink-0"><MapPin size={10} /> {photo.gps.lat.toFixed(5)}, {photo.gps.lng.toFixed(5)}</span>}
          </div>
          <div className="flex gap-1 items-center">
            <button className="text-white/40 hover:text-white h-7 px-2" onClick={() => setZoom(z => Math.min(z + 0.25, 4))}><ZoomIn size={14} /></button>
            <button className="text-white/40 hover:text-white h-7 px-2" onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}><ZoomOut size={14} /></button>
            <button className="text-white/40 hover:text-white h-7 px-2" onClick={() => setRotation(r => r + 90)}><RotateCw size={14} /></button>
            <a href={mediaAPI.streamUrl(photo.id, token)} download className="text-white/40 hover:text-emerald-400 h-7 px-2 flex items-center"><Download size={14} /></a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== MAIN MEDIA PAGE =====
const MediaPage = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const canManage = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'supervisor';
  const canUpload = !isSuperAdmin && ['admin', 'supervisor', 'leader', 'pilot'].includes(user?.role);
  const isPilot = user?.role === 'pilot';

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadingRef = useRef(false);
  const speedTracker = useRef({ bytes: 0, timestamps: [] });
  const uploadQueueRef = useRef([]);
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const mountedRef = useRef(true);
  const filesMapRef = useRef({}); // Store File objects outside React state
  const uploadIdsRef = useRef({}); // Map queueIndex -> upload_id for abort
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; cancelledRef.current = true; }; }, []);
  useEffect(() => { uploadQueueRef.current = uploadQueue; }, [uploadQueue]);

  // Warn user before leaving page during active upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = 'Upload em andamento. Sair pode interromper o envio.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  // Classify error for clearer user feedback
  const classifyError = useCallback((err) => {
    if (!err) return { type: 'unknown', label: 'Erro desconhecido' };
    if (!err.response) return { type: 'network', label: 'Sem conexao' };
    const status = err.response.status;
    if (status === 401) return { type: 'auth', label: 'Sessao expirada' };
    if (status === 403) return { type: 'permission', label: 'Sem permissao' };
    if (status === 404) return { type: 'expired', label: 'Upload expirado' };
    if (status === 413) return { type: 'size', label: 'Arquivo muito grande' };
    if (status === 400) return { type: 'validation', label: err.response.data?.detail || 'Dados invalidos' };
    if (status >= 500) return { type: 'server', label: 'Erro no servidor' };
    return { type: 'unknown', label: `Erro ${status}` };
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        const vProjects = isSuperAdmin ? res.data : res.data.filter(p => p.has_videos);
        setProjects(vProjects);
        if (vProjects.length > 0) {
          const userProject = vProjects.find(p => p.id === user?.project_id);
          setSelectedProject(userProject ? userProject.id : vProjects[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id, isSuperAdmin]);

  const fetchMedia = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await mediaAPI.list(selectedProject, currentFolder);
      setItems(res.data);
      if (currentFolder) {
        const bcRes = await mediaAPI.breadcrumb(selectedProject, currentFolder);
        setBreadcrumb(bcRes.data);
      } else { setBreadcrumb([]); }
    } catch { toast.error('Erro ao carregar midias'); }
    finally { setLoading(false); }
  }, [selectedProject, currentFolder]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  useEffect(() => {
    if (!selectedProject) return;
    mediaAPI.storageStats(selectedProject).then(r => setStorageStats(r.data)).catch(() => {});
  }, [selectedProject, items.length]);

  // Poll for video transcoding status (stable ref-based, no re-render loop)
  const pollingRef = useRef(null);
  useEffect(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (!selectedProject) return;
    const hasProcessing = items.some(i => i.media_type === 'video' && i.transcode_status === 'processing');
    if (!hasProcessing) return;
    pollingRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      mediaAPI.list(selectedProject, currentFolder).then(res => {
        if (mountedRef.current) setItems(res.data);
      }).catch(() => {});
    }, 15000); // 15s interval to reduce load
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [selectedProject, currentFolder]); // NO items dependency to prevent loop

  // ===== NAVIGATION =====
  const openFolder = (folder) => setCurrentFolder(folder.id);
  const navigateToRoot = () => { setCurrentFolder(null); setBreadcrumb([]); };
  const navigateToBreadcrumb = (folderId) => setCurrentFolder(folderId);

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await mediaAPI.createFolder(selectedProject, { name: newFolderName.trim(), parent_id: currentFolder });
      toast.success('Pasta criada!');
      setNewFolderName('');
      setShowNewFolder(false);
      fetchMedia();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar pasta'); }
  };

  // ===== UPLOAD CONTROLS =====
  const pauseUpload = () => { pausedRef.current = true; setIsPaused(true); toast('Upload pausado'); };
  const resumeUpload = () => { pausedRef.current = false; setIsPaused(false); toast.success('Upload retomado'); };
  const cancelUpload = async () => {
    cancelledRef.current = true;
    pausedRef.current = false;
    setIsPaused(false);
    uploadingRef.current = false;
    setIsUploading(false);
    // Abort any in-flight upload IDs on server (best-effort)
    const idsToAbort = Object.values(uploadIdsRef.current).filter(Boolean);
    uploadIdsRef.current = {};
    setUploadQueue([]);
    filesMapRef.current = {};
    Promise.all(idsToAbort.map(uid => mediaAPI.abortUpload(selectedProject, uid).catch(() => {}))).catch(() => {});
    toast('Upload cancelado');
  };

  const waitIfPaused = async () => {
    while (pausedRef.current && !cancelledRef.current) {
      await new Promise(r => setTimeout(r, 300));
    }
  };

  // ===== UPLOAD ENGINE =====
  const trackSpeed = (bytesJustUploaded) => {
    const now = Date.now();
    speedTracker.current.timestamps.push({ time: now, bytes: bytesJustUploaded });
    speedTracker.current.timestamps = speedTracker.current.timestamps.filter(t => now - t.time < 5000);
    if (speedTracker.current.timestamps.length > 1) {
      const w = speedTracker.current.timestamps;
      const elapsed = (w[w.length - 1].time - w[0].time) / 1000;
      const total = w.reduce((s, t) => s + t.bytes, 0);
      return elapsed > 0 ? total / elapsed : 0;
    }
    return 0;
  };

  const uploadChunkWithRetry = async (file, projectId, uploadId, chunkIndex, totalChunks, queueIndex) => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    const chunkBytes = end - start;
    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      await waitIfPaused();
      if (cancelledRef.current) return;
      try {
        const fd = new FormData();
        fd.append('file', chunk, `chunk_${chunkIndex}`);
        await mediaAPI.uploadChunk(projectId, uploadId, chunkIndex, fd);
        const speed = trackSpeed(chunkBytes);
        if (!mountedRef.current) return;
        setUploadQueue(prev => prev.map((it, idx) => {
          if (idx !== queueIndex) return it;
          const done = (it._completedChunks || 0) + 1;
          return { ...it, progress: Math.round((done / totalChunks) * 100), speed, status: 'uploading', _completedChunks: done, errorLabel: null };
        }));
        return;
      } catch (err) {
        if (cancelledRef.current) return;
        lastError = err;
        const cls = classifyError(err);
        // Don't retry permanent errors
        if (['auth', 'permission', 'size', 'validation', 'expired'].includes(cls.type)) {
          throw err;
        }
        if (attempt < MAX_RETRIES - 1) {
          if (!mountedRef.current) return;
          setUploadQueue(prev => prev.map((it, idx) => idx === queueIndex ? { ...it, status: 'retrying', errorLabel: cls.label } : it));
          await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt)));
        } else { throw err; }
      }
    }
    if (lastError) throw lastError;
  };

  const uploadFile = async (file, queueIndex, targetFolderId) => {
    if (cancelledRef.current) return;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const folderId = targetFolderId !== undefined ? targetFolderId : currentFolder;
    try {
      setUploadQueue(prev => prev.map((it, idx) => idx === queueIndex ? { ...it, status: 'uploading', _completedChunks: 0, errorLabel: null } : it));
      const initRes = await mediaAPI.initUpload(selectedProject, { filename: file.name, file_size: file.size, total_chunks: totalChunks, folder_id: folderId, chunk_size: CHUNK_SIZE });
      const uploadId = initRes.data.upload_id;
      uploadIdsRef.current[queueIndex] = uploadId;
      for (let i = 0; i < totalChunks; i += PARALLEL_CHUNKS) {
        if (cancelledRef.current) return;
        await waitIfPaused();
        const batch = [];
        for (let j = i; j < Math.min(i + PARALLEL_CHUNKS, totalChunks); j++) {
          batch.push(uploadChunkWithRetry(file, selectedProject, uploadId, j, totalChunks, queueIndex));
        }
        await Promise.all(batch);
      }
      if (cancelledRef.current) return;
      await mediaAPI.completeUpload(selectedProject, { upload_id: uploadId });
      delete uploadIdsRef.current[queueIndex];
      setUploadQueue(prev => prev.map((it, idx) => idx === queueIndex ? { ...it, progress: 100, status: 'done', speed: 0, errorLabel: null } : it));
    } catch (err) {
      if (!cancelledRef.current) {
        const cls = classifyError(err);
        console.error(`Upload error [${cls.type}]:`, err?.response?.data || err?.message);
        setUploadQueue(prev => prev.map((it, idx) => idx === queueIndex ? { ...it, status: 'error', speed: 0, errorLabel: cls.label, errorType: cls.type } : it));
      }
    }
  };

  const retryUpload = async (queueIndex) => {
    const file = filesMapRef.current[queueIndex];
    const item = uploadQueueRef.current[queueIndex];
    if (!file || !item || item.status !== 'error') return;
    uploadingRef.current = true;
    setIsUploading(true);
    setUploadQueue(prev => prev.map((q, i) => i === queueIndex ? { ...q, status: 'pending', progress: 0, _completedChunks: 0, speed: 0 } : q));
    await uploadFile(file, queueIndex, item.folderId || currentFolder);
    const current = uploadQueueRef.current;
    if (!current.some(q => ['uploading', 'retrying', 'pending'].includes(q.status))) {
      uploadingRef.current = false;
      setIsUploading(false);
      fetchMedia();
    }
  };

  const MAX_AUTO_RETRIES = 3;

  const retryAllFailed = async () => {
    const queue = uploadQueueRef.current;
    const failed = queue.map((q, i) => q.status === 'error' ? i : -1).filter(i => i >= 0);
    if (!failed.length) return;

    uploadingRef.current = true;
    cancelledRef.current = false;
    setIsUploading(true);
    setUploadQueue(prev => prev.map((q, i) => failed.includes(i) ? { ...q, status: 'pending', progress: 0, _completedChunks: 0, speed: 0 } : q));

    for (let i = 0; i < failed.length; i += MAX_CONCURRENT_UPLOADS) {
      if (cancelledRef.current) break;
      const batch = failed.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map(idx => {
        const file = filesMapRef.current[idx];
        const item = uploadQueueRef.current[idx];
        if (!file) return Promise.resolve();
        return uploadFile(file, idx, item?.folderId || currentFolder);
      }));
    }

    uploadingRef.current = false;
    setIsUploading(false);

    const stillFailed = uploadQueueRef.current.filter(q => q.status === 'error').length;
    if (stillFailed === 0) {
      toast.success('Todos os arquivos enviados!');
      fetchMedia();
      setTimeout(() => { setUploadQueue([]); filesMapRef.current = {}; }, 3000);
    } else {
      toast.error(`${stillFailed} arquivo(s) falharam. Clique REINICIAR para tentar novamente.`);
    }
  };

  const processUploadQueue = async (files, targetFolderId) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    cancelledRef.current = false;
    pausedRef.current = false;
    setIsPaused(false);
    setIsUploading(true);
    speedTracker.current = { bytes: 0, timestamps: [] };
    const validFiles = [];
    const seen = new Set();
    let dupCount = 0;
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!VALID_EXTS.includes(ext)) { toast.error(`Formato nao suportado: ${file.name}`); continue; }
      if (file.size <= 0) { toast.error(`Arquivo vazio: ${file.name}`); continue; }
      const key = `${file.name}_${file.size}`;
      if (seen.has(key)) { dupCount++; continue; }
      seen.add(key);
      validFiles.push({ file, name: file.name, size: file.size, type: getMediaType(file.name), progress: 0, status: 'pending', speed: 0, _completedChunks: 0 });
    }
    if (dupCount > 0) toast(`${dupCount} duplicata(s) ignorada(s)`);
    if (!validFiles.length) { uploadingRef.current = false; setIsUploading(false); return; }
    // Store File objects in ref (not in React state to avoid serialization/render issues)
    const fileMap = {};
    validFiles.forEach((v, i) => { fileMap[i] = v.file; });
    filesMapRef.current = fileMap;
    // Queue items WITHOUT file objects
    const queueItems = validFiles.map(v => ({ name: v.name, size: v.size, type: v.type, progress: 0, status: 'pending', speed: 0, _completedChunks: 0 }));
    setUploadQueue(queueItems);
    for (let i = 0; i < validFiles.length; i += MAX_CONCURRENT_UPLOADS) {
      if (cancelledRef.current) break;
      const batch = validFiles.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map((it, bi) => uploadFile(it.file, i + bi, targetFolderId)));
    }
    uploadingRef.current = false;
    setIsUploading(false);
    if (isPilot) setUploadComplete(true);

    const failedCount = uploadQueueRef.current.filter(q => q.status === 'error').length;
    const doneCount = uploadQueueRef.current.filter(q => q.status === 'done').length;

    if (failedCount > 0) {
      toast.error(`${failedCount} arquivo(s) com erro. Clique REINICIAR.`);
      // Keep queue visible - don't auto-clear
    } else {
      if (doneCount > 0) toast.success(`${doneCount} arquivo(s) enviado(s)!`);
      fetchMedia();
      setTimeout(() => { if (mountedRef.current) { setUploadQueue([]); filesMapRef.current = {}; } }, 3000);
    }
  };

  const handleFolderUpload = async (files) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    cancelledRef.current = false;
    pausedRef.current = false;
    setIsPaused(false);
    setIsUploading(true);
    speedTracker.current = { bytes: 0, timestamps: [] };
    const folderMap = {};
    const allUploads = [];
    const sorted = [...files].sort((a, b) => (a.webkitRelativePath || '').split('/').length - (b.webkitRelativePath || '').split('/').length);
    for (const file of sorted) {
      const rel = file.webkitRelativePath || file.name;
      const parts = rel.split('/');
      if (parts.length <= 1) {
        const ext = file.name.split('.').pop().toLowerCase();
        if (VALID_EXTS.includes(ext)) allUploads.push({ file, folderId: currentFolder });
        continue;
      }
      const folderParts = parts.slice(0, -1);
      let parentId = currentFolder;
      for (let d = 0; d < folderParts.length; d++) {
        const path = folderParts.slice(0, d + 1).join('/');
        if (!folderMap[path]) {
          try {
            const res = await mediaAPI.createFolder(selectedProject, { name: folderParts[d], parent_id: parentId });
            folderMap[path] = res.data.id;
          } catch (e) {
            folderMap[path] = e.response?.data?.id || parentId;
          }
        }
        parentId = folderMap[path];
      }
      const ext = file.name.split('.').pop().toLowerCase();
      if (VALID_EXTS.includes(ext)) allUploads.push({ file, folderId: folderMap[folderParts.join('/')] || currentFolder });
    }
    if (!allUploads.length) { uploadingRef.current = false; setIsUploading(false); return; }
    // Store File objects in ref
    const fileMap = {};
    allUploads.forEach((u, i) => { fileMap[i] = u.file; });
    filesMapRef.current = fileMap;
    // Queue items WITHOUT file objects (folderIds stored separately)
    const folderIds = {};
    const queueItems = allUploads.map((u, i) => { folderIds[i] = u.folderId; return { name: u.file.name, size: u.file.size, type: getMediaType(u.file.name), folderId: u.folderId, progress: 0, status: 'pending', speed: 0, _completedChunks: 0 }; });
    setUploadQueue(queueItems);
    for (let i = 0; i < allUploads.length; i += MAX_CONCURRENT_UPLOADS) {
      if (cancelledRef.current) break;
      const batch = allUploads.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map((it, bi) => uploadFile(it.file, i + bi, it.folderId)));
    }
    uploadingRef.current = false;
    setIsUploading(false);
    if (isPilot) setUploadComplete(true);

    const failedCount = uploadQueueRef.current.filter(q => q.status === 'error').length;
    if (failedCount > 0) {
      toast.error(`${failedCount} arquivo(s) com erro. Clique REINICIAR.`);
    } else {
      toast.success(`Upload concluido! ${allUploads.length} arquivo(s)`);
      fetchMedia();
      setTimeout(() => { if (mountedRef.current) { setUploadQueue([]); filesMapRef.current = {}; } }, 3000);
    }
  };

  const handleFileSelect = (e) => { const f = Array.from(e.target.files || []); if (f.length) processUploadQueue(f); if (fileInputRef.current) fileInputRef.current.value = ''; };
  const handleFolderSelect = (e) => { const f = Array.from(e.target.files || []); if (f.length) handleFolderUpload(f); if (folderInputRef.current) folderInputRef.current.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = Array.from(e.dataTransfer.files || []); if (f.length) processUploadQueue(f); };

  const handleDelete = async (item) => {
    const label = item.media_type === 'folder' ? 'pasta' : item.media_type === 'photo' ? 'foto' : 'video';
    if (!window.confirm(`Excluir ${label} "${item.name || item.original_filename}"?`)) return;
    if (!selectedProject) { toast.error('Selecione um projeto'); return; }
    try {
      await mediaAPI.delete(selectedProject, item.id);
      toast.success(`${label} excluido(a)!`);
      fetchMedia();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  const getShareUrl = (item) => `${window.location.origin}/media/${item.share_token}`;
  const copyShareLink = (item) => { navigator.clipboard.writeText(getShareUrl(item)); toast.success('Link copiado!'); };
  const shareWhatsApp = (item) => { window.open(`https://wa.me/?text=${encodeURIComponent((item.name || item.original_filename) + ' ' + getShareUrl(item))}`, '_blank'); };
  const shareEmail = (item) => { window.location.href = `mailto:?subject=${encodeURIComponent('RPAS: ' + (item.name || item.original_filename))}&body=${encodeURIComponent(getShareUrl(item))}`; };

  const folders = useMemo(() => items.filter(i => i.media_type === 'folder'), [items]);
  const photos = useMemo(() => items.filter(i => i.media_type === 'photo'), [items]);
  const videos = useMemo(() => items.filter(i => i.media_type === 'video'), [items]);
  const projectName = useMemo(() => projects.find(p => p.id === selectedProject)?.name || '', [projects, selectedProject]);
  const hasQueueErrors = useMemo(() => uploadQueue.some(q => q.status === 'error'), [uploadQueue]);

  return (
    <UploadErrorBoundary>
    <div className="space-y-5" data-testid="media-page">
      {/* ===== HEADER ===== */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0a1628] uppercase tracking-wider flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            <HardDrive size={20} className="text-cyan-500" /> Midias do Projeto
          </h2>
          {projectName && <p className="text-xs text-gray-400 font-mono mt-0.5">{projectName}</p>}
        </div>
        {(isSuperAdmin || projects.length > 1) && (
          <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setCurrentFolder(null); }}>
            <SelectTrigger className="w-[220px] h-9 bg-[#0f1923] border-white/10 text-white text-xs rounded-lg" data-testid="media-project-select">
              <SelectValue placeholder="Selecione o projeto" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a2332] border-white/10">
              {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-white/80 text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ===== STORAGE STATS (Dark panel) ===== */}
      {storageStats && storageStats.total_size !== undefined && (
        <div className="bg-[#0f1923] rounded-xl p-5 border border-white/5" data-testid="storage-stats">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[11px] font-bold text-white/60 uppercase tracking-[0.15em] flex items-center gap-2">
              <HardDrive size={13} className="text-cyan-400" /> ARMAZENAMENTO EM NUVEM
            </h3>
            <span className="text-[10px] font-mono text-white/30">{storageStats.total_count} arquivo(s)</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2.5" />
                {storageStats.total_size > 0 && storageStats.photo_size != null && (
                  <>
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#06b6d4" strokeWidth="2.5"
                      strokeDasharray={`${(storageStats.photo_size / storageStats.total_size) * 94.2} 94.2`} strokeLinecap="round" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#a855f7" strokeWidth="2.5"
                      strokeDasharray={`${(storageStats.video_size / storageStats.total_size) * 94.2} 94.2`}
                      strokeDashoffset={`${-(storageStats.photo_size / storageStats.total_size) * 94.2}`} strokeLinecap="round" />
                  </>
                )}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">{formatSize(storageStats.total_size)}</span>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              {[
                { color: 'bg-cyan-400', label: 'Fotos', count: storageStats.photo_count, size: storageStats.photo_size },
                { color: 'bg-purple-500', label: 'Videos', count: storageStats.video_count, size: storageStats.video_size },
                { color: 'bg-amber-400', label: 'Pastas', count: storageStats.folder_count, size: null },
                { color: 'bg-emerald-400', label: 'Total', count: storageStats.total_count, size: storageStats.total_size },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${s.color}`} />
                  <div>
                    <p className="text-[9px] font-mono text-white/30">{s.label} ({s.count})</p>
                    <p className="text-[11px] font-bold text-white/80">{s.size !== null ? formatSize(s.size) : s.count}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== BREADCRUMB ===== */}
      {(currentFolder || breadcrumb.length > 0) && (
        <div className="flex items-center gap-1.5 text-xs font-mono" data-testid="media-breadcrumb">
          <button onClick={navigateToRoot} className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"><ArrowLeft size={12} /> Raiz</button>
          {breadcrumb.map((bc, idx) => (
            <span key={bc.id} className="flex items-center gap-1">
              <ChevronRight size={12} className="text-white/20" />
              {idx === breadcrumb.length - 1 ? <span className="text-white/80 font-medium">{bc.name}</span> : <button onClick={() => navigateToBreadcrumb(bc.id)} className="text-cyan-400 hover:text-cyan-300">{bc.name}</button>}
            </span>
          ))}
        </div>
      )}

      {/* ===== UPLOAD ZONE ===== */}
      {canUpload && selectedProject && (user?.project_ids?.includes(selectedProject) || user?.project_id === selectedProject) && (
        <div
          className={`bg-[#0f1923] rounded-xl border transition-all ${dragOver ? 'border-cyan-400 bg-cyan-500/5 scale-[1.005]' : isUploading || hasQueueErrors ? 'border-cyan-500/20' : 'border-dashed border-white/10 hover:border-cyan-500/30'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="media-upload-zone"
        >
          <input ref={fileInputRef} type="file" className="hidden" multiple accept=".jpg,.jpeg,.png,.mp4,.mov,.avi,.mkv,.webm,.wmv,.flv,.m4v,.gif,.webp,.bmp,.heic,.heif,.tiff,.tif,.svg,.mrk,.nav,.obs,.rtk,.bin,.pdf" onChange={handleFileSelect} data-testid="media-file-input" />
          <input ref={folderInputRef} type="file" className="hidden" webkitdirectory="" multiple onChange={handleFolderSelect} data-testid="media-folder-input" />

          {isUploading ? (
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                    {isPaused ? <Pause size={16} className="text-amber-400" /> : <Loader2 size={16} className="text-cyan-400 animate-spin" />}
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-semibold">{isPaused ? 'Upload Pausado' : 'Enviando Arquivos'}</p>
                    <p className="text-white/30 text-[10px] font-mono">{uploadQueue.filter(q => q.status === 'done').length} de {uploadQueue.length} concluidos</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Pause / Resume */}
                  {isPaused ? (
                    <Button onClick={resumeUpload} size="sm" className="h-7 px-3 text-[10px] bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg" data-testid="resume-upload-btn">
                      <Play size={10} className="mr-1" /> RETOMAR
                    </Button>
                  ) : (
                    <Button onClick={pauseUpload} size="sm" className="h-7 px-3 text-[10px] bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg" data-testid="pause-upload-btn">
                      <Pause size={10} className="mr-1" /> PAUSAR
                    </Button>
                  )}
                  {/* Cancel */}
                  <Button onClick={cancelUpload} size="sm" className="h-7 px-3 text-[10px] bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg" data-testid="cancel-upload-btn">
                    <XCircle size={10} className="mr-1" /> CANCELAR
                  </Button>
                  {/* Stats */}
                  {(() => {
                    const totalB = uploadQueue.reduce((s, q) => s + q.size, 0);
                    const upB = uploadQueue.reduce((s, q) => s + (q.size * q.progress / 100), 0);
                    const pct = totalB > 0 ? Math.round((upB / totalB) * 100) : 0;
                    const active = uploadQueue.find(q => q.speed > 0);
                    const spd = active?.speed || 0;
                    const rem = totalB - upB;
                    const eta = spd > 0 ? Math.ceil(rem / spd) : 0;
                    return (
                      <div className="flex items-center gap-3 text-[10px] font-mono ml-2">
                        <span className="text-cyan-400 font-bold">{pct}%</span>
                        {spd > 0 && <span className="text-emerald-400">{formatSize(spd)}/s</span>}
                        {eta > 0 && <span className="text-white/30">ETA {eta > 60 ? `${Math.floor(eta/60)}m` : `${eta}s`}</span>}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Global progress bar */}
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-300" style={{ width: `${uploadQueue.length ? Math.round(uploadQueue.reduce((s, q) => s + q.progress, 0) / uploadQueue.length) : 0}%` }} />
              </div>
              <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                {uploadQueue.map((item, idx) => <UploadQueueItem key={idx} item={item} onRetry={retryUpload} index={idx} />)}
              </div>
              {hasQueueErrors && (
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <span className="text-red-400 text-[10px] font-mono flex items-center gap-1"><AlertCircle size={11} /> {uploadQueue.filter(q => q.status === 'error').length} com erro</span>
                  <Button onClick={retryAllFailed} size="sm" className="h-7 px-3 text-[10px] bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg" data-testid="retry-all-btn">
                    <RefreshCw size={10} className="mr-1" /> REINICIAR TODOS
                  </Button>
                </div>
              )}
            </div>
          ) : hasQueueErrors ? (
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertCircle size={16} className="text-red-400" /></div>
                  <div>
                    <p className="text-white/90 text-sm font-semibold">Uploads com Erro</p>
                    <p className="text-white/30 text-[10px] font-mono">{uploadQueue.filter(q => q.status === 'done').length} ok, {uploadQueue.filter(q => q.status === 'error').length} com erro</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setUploadQueue([])} variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-white/30 hover:text-white/60"><X size={11} className="mr-1" /> Limpar</Button>
                  <Button onClick={retryAllFailed} size="sm" className="h-7 px-3 text-[10px] bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg" data-testid="retry-all-btn">
                    <RefreshCw size={10} className="mr-1" /> REINICIAR TODOS
                  </Button>
                </div>
              </div>
              <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                {uploadQueue.map((item, idx) => <UploadQueueItem key={idx} item={item} onRetry={retryUpload} index={idx} />)}
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between flex-col sm:flex-row sm:items-center gap-3 mb-5">
                <div>
                  <p className="text-white/90 text-sm font-semibold flex items-center gap-2">
                    <Upload size={15} className="text-cyan-400" /> Importar Midias
                  </p>
                  <p className="text-white/30 text-[10px] font-mono mt-1">Foto (grande-angular / zoom / IR) | Video | Panoramica | PPK | PDF</p>
                </div>
                <div className="flex gap-2 flex-wrap w-full sm:w-auto">
                  <Button onClick={() => fileInputRef.current?.click()} className="h-10 sm:h-8 px-4 text-xs sm:text-[10px] bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white rounded-lg font-semibold flex-1 sm:flex-none touch-manipulation" data-testid="upload-files-btn">
                    <Upload size={12} className="mr-1.5" /> IMPORTAR ARQUIVO
                  </Button>
                  <Button onClick={() => folderInputRef.current?.click()} className="h-10 sm:h-8 px-4 text-xs sm:text-[10px] bg-transparent border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 active:bg-cyan-500/20 rounded-lg font-semibold flex-1 sm:flex-none touch-manipulation" data-testid="upload-folder-btn">
                    <FolderPlus size={12} className="mr-1.5" /> IMPORTAR PASTA
                  </Button>
                  <Button onClick={() => setShowNewFolder(true)} className="h-10 sm:h-8 px-4 text-xs sm:text-[10px] bg-transparent border border-white/10 text-white/40 hover:bg-white/5 active:bg-white/10 rounded-lg font-semibold flex-1 sm:flex-none touch-manipulation" data-testid="create-folder-btn">
                    <FolderPlus size={12} className="mr-1.5" /> NOVA PASTA
                  </Button>
                </div>
              </div>
              {/* Supported formats + Drop zone */}
              <div className="border border-dashed border-white/10 rounded-xl py-8 sm:py-10 text-center hover:border-cyan-500/20 transition-colors">
                <UploadCloud size={36} className="text-white/10 mx-auto mb-3" />
                <p className="text-white/40 text-xs font-medium">Arraste arquivos ou pastas aqui</p>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-3">
                  {['.jpg', '.jpeg', '.png', '.mp4', '.mov', '.mrk', '.nav', '.obs', '.rtk', '.bin'].map(f => (
                    <span key={f} className="text-[9px] font-mono text-white/20 bg-white/5 px-1.5 py-0.5 rounded">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pilot upload complete */}
      {isPilot && uploadComplete && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 text-center" data-testid="upload-complete-msg">
          <CheckCircle2 size={32} className="text-emerald-400 mx-auto mb-2" />
          <p className="text-white/90 font-semibold text-sm">Upload Concluido</p>
          <p className="text-white/40 text-xs mt-1">Arquivos enviados com sucesso</p>
          <Button className="mt-3 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] rounded-lg h-8" onClick={() => setUploadComplete(false)} data-testid="upload-more-btn">
            <Upload size={11} className="mr-1" /> ENVIAR MAIS
          </Button>
        </div>
      )}

      {/* ===== CONTENT GRID ===== */}
      {!isPilot && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" /></div>
          ) : items.length > 0 ? (
            <div className="space-y-6">
              {folders.length > 0 && (
                <div>
                  <h3 className="text-[11px] font-bold text-white/40 uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5"><Folder size={12} className="text-amber-400" /> PASTAS ({folders.length})</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="folder-grid">
                    {folders.map(f => <FolderCard key={f.id} item={f} canDelete={canManage} onOpen={openFolder} onDelete={handleDelete} onShare={setShareItem} />)}
                  </div>
                </div>
              )}
              {photos.length > 0 && (
                <div>
                  <h3 className="text-[11px] font-bold text-white/40 uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5"><Camera size={12} className="text-cyan-400" /> FOTOS ({photos.length})</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="photo-grid">
                    {photos.map(p => <PhotoCard key={p.id} item={p} token={token} canDelete={canManage} onDelete={handleDelete} onShare={setShareItem} onView={setViewingPhoto} />)}
                  </div>
                </div>
              )}
              {videos.length > 0 && (
                <div>
                  <h3 className="text-[11px] font-bold text-white/40 uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5"><Video size={12} className="text-purple-400" /> VIDEOS ({videos.length})</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="video-grid">
                    {videos.map(v => <VideoCard key={v.id} item={v} token={token} canDelete={canManage} onDelete={handleDelete} onShare={setShareItem} onPlay={setPlayingVideo} />)}
                  </div>
                </div>
              )}
            </div>
          ) : selectedProject ? (
            <div className="bg-[#0f1923] rounded-xl p-12 text-center border border-white/5">
              <UploadCloud size={40} className="text-white/10 mx-auto mb-3" />
              <p className="text-white/40 font-mono text-sm">{currentFolder ? 'Pasta vazia' : 'Nenhuma midia'}</p>
              {canUpload && <p className="text-white/20 text-xs mt-2">Arraste arquivos ou clique para importar</p>}
            </div>
          ) : (
            <div className="bg-[#0f1923] rounded-xl p-12 text-center border border-white/5">
              <HardDrive size={40} className="text-white/10 mx-auto mb-3" />
              <p className="text-white/40 font-mono text-sm">Selecione um projeto</p>
            </div>
          )}
        </>
      )}

      {/* ===== VIDEO PLAYER ===== */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="bg-black border-none max-w-4xl p-0 overflow-hidden" aria-describedby={undefined}>
          <DialogHeader className="sr-only"><DialogTitle>Video Player</DialogTitle></DialogHeader>
          <div className="relative">
            <button onClick={() => setPlayingVideo(null)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" aria-label="Fechar video" data-testid="close-video-player"><X size={18} /></button>
            {playingVideo && (
              <video
                key={playingVideo.id}
                src={mediaAPI.streamUrl(playingVideo.id, token)}
                className="w-full max-h-[80vh]"
                controls
                autoPlay
                playsInline
                preload="auto"
                controlsList="nodownload"
                onError={(e) => { e.target.poster = ''; toast.error('Erro ao carregar video'); }}
                data-testid="video-player"
              />
            )}
          </div>
          {playingVideo && (
            <div className="px-4 py-2 bg-[#0a1628] flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Video size={14} className="text-purple-400 flex-shrink-0" />
                <p className="text-white text-sm truncate">{playingVideo.original_filename}</p>
                <span className="text-white/30 text-[10px] font-mono flex-shrink-0">{formatSize(playingVideo.size)}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href={mediaAPI.streamUrl(playingVideo.id, token)} download className="text-white/40 hover:text-emerald-400 text-[10px] flex items-center gap-1"><Download size={11} /> BAIXAR</a>
                <button className="text-white/40 hover:text-cyan-400 text-[10px] flex items-center gap-1" onClick={() => { setPlayingVideo(null); setShareItem(playingVideo); }}><Link2 size={11} /> COMPARTILHAR</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* LIGHTBOX */}
      {viewingPhoto && <PhotoLightbox photo={viewingPhoto} token={token} onClose={() => setViewingPhoto(null)} items={photos} onNavigate={setViewingPhoto} />}

      {/* SHARE MODAL */}
      <Dialog open={!!shareItem} onOpenChange={() => setShareItem(null)}>
        <DialogContent className="bg-[#1a2332] border-white/10 max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="text-white/90 text-sm uppercase tracking-wider">Compartilhar</DialogTitle></DialogHeader>
          {shareItem && (
            <div className="space-y-3 mt-3">
              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  {shareItem.media_type === 'folder' ? <Folder size={14} className="text-amber-400" /> : shareItem.media_type === 'photo' ? <Camera size={14} className="text-cyan-400" /> : <Video size={14} className="text-purple-400" />}
                  <p className="text-white/90 text-sm font-medium truncate">{shareItem.name || shareItem.original_filename}</p>
                </div>
                <p className="text-white/30 text-[10px] font-mono">{formatSize(shareItem.size)} | {formatDate(shareItem.created_at)}</p>
              </div>
              <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3">
                <p className="text-white/30 text-[10px] font-mono uppercase mb-1">Link compartilhavel</p>
                <p className="text-cyan-400 text-xs font-mono break-all" data-testid="share-link-url">{getShareUrl(shareItem)}</p>
              </div>
              <Button onClick={() => copyShareLink(shareItem)} className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-xs rounded-lg" data-testid="copy-share-link"><Copy size={14} className="mr-2" /> COPIAR LINK</Button>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => shareWhatsApp(shareItem)} className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 rounded-lg h-9 text-xs" data-testid="share-whatsapp"><Phone size={14} className="mr-1" /> WhatsApp</Button>
                <Button onClick={() => shareEmail(shareItem)} className="bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 rounded-lg h-9 text-xs" data-testid="share-email"><Mail size={14} className="mr-1" /> E-mail</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW FOLDER */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="bg-[#1a2332] border-white/10 max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="text-white/90 text-sm uppercase tracking-wider">Nova Pasta</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Nome da pasta" className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-lg" onKeyDown={(e) => e.key === 'Enter' && createFolder()} autoFocus data-testid="new-folder-name-input" />
            <div className="flex gap-2">
              <Button onClick={createFolder} className="bg-cyan-500 hover:bg-cyan-600 text-white text-xs flex-1 rounded-lg" disabled={!newFolderName.trim()} data-testid="create-folder-confirm"><FolderPlus size={14} className="mr-1" /> CRIAR</Button>
              <Button onClick={() => setShowNewFolder(false)} className="bg-white/5 text-white/40 hover:bg-white/10 text-xs rounded-lg" data-testid="create-folder-cancel">CANCELAR</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </UploadErrorBoundary>
  );
};

export default MediaPage;
